import type { ApolloError } from '@apollo/client';
import type { Dispatch, FC, ReactNode } from 'react';
import { createContext, useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { BrandKey, CategoryFormat } from '../../__generated__/queries-web';
import type { Category, CategoryList } from './types';
import { getActivePathCategoryId } from './utils';

const INITIAL_ACTIVE_CATEGORY: Category | null = null;
const INITIAL_CATEGORIES: CategoryList[] | null = [];

// CONTEXT
interface CategoriesContextProps {
  activeCategory: Category | null;
  activeCategoryRouteId: string | null;
  brandKey?: BrandKey;
  categories: CategoryList[];
  categoriesError: ApolloError | null | undefined;
  format?: CategoryFormat;
  isCategoriesLoading: boolean;
  refetchCategories: () => void;
  setActiveCategory: Dispatch<Category | null>;
  setCategories: Dispatch<CategoryList[]>;
}
const CategoriesContext = createContext<CategoriesContextProps>({
  activeCategory: INITIAL_ACTIVE_CATEGORY,
  activeCategoryRouteId: null,
  categories: INITIAL_CATEGORIES,
  categoriesError: null,
  isCategoriesLoading: false,
  refetchCategories: () => null,
  setActiveCategory: () => null,
  setCategories: () => null,
});

// PROVIDER
interface CategoriesProviderProps {
  brandKey: BrandKey | undefined;
  children: ReactNode;
  format: CategoryFormat | undefined;
}
const CategoriesProvider: FC<CategoriesProviderProps> = ({
  brandKey,
  children,
  format,
}) => {
  const routeParams = useParams();
  const activeCategoryRouteId = useMemo(
    () => getActivePathCategoryId(routeParams),
    [routeParams],
  );

  const [activeCategory, setActiveCategoryState] = useState<Category | null>(
    INITIAL_ACTIVE_CATEGORY,
  );
  const [categories, setCategories] = useState<CategoryList[]>(
    INITIAL_CATEGORIES,
  );

  const setActiveCategory = useCallback((category: Category | null) => {
    setActiveCategoryState(category);
  }, []);

  const refetchCategories = useCallback(() => {
    // No-op since individual components handle their own data loading
  }, []);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => {
    return {
      activeCategory,
      activeCategoryRouteId,
      brandKey,
      categories,
      categoriesError: null,
      format,
      isCategoriesLoading: false,
      refetchCategories,
      setActiveCategory,
      setCategories,
    };
  }, [
    activeCategory,
    activeCategoryRouteId,
    brandKey,
    categories,
    format,
    refetchCategories,
    setActiveCategory,
    setCategories,
  ]);

  return (
    <CategoriesContext.Provider value={contextValue}>
      {children}
    </CategoriesContext.Provider>
  );
};

export { CategoriesContext, CategoriesProvider };
